import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Calendar, Plus, Users, FileText, FileSignature, Briefcase, Settings, Activity, LogOut, Shield, Building2, Lock, CreditCard, MessageSquare, MessagesSquare, RefreshCw, CalendarClock, DollarSign, Package, BarChart2, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Event } from '../types/Event';
import { useAuth } from '../contexts/AuthContext';
import UserProfileModal from './UserProfileModal';

interface SidebarProps {
  isOpen: boolean;
  events: Event[];
  onEventClick: (event: Event) => void;
  onAddEvent: () => void;
  currentView: string;
  onViewChange: (view: string) => void;
  onClose: () => void;
  isMobile?: boolean;
  clinicName?: string;
  clientCount?: number;
  unreadChatCount?: number;
}

type NavKey = 'calendar' | 'feed' | 'clients' | 'procedures' | 'medical-records' | 'bulk-message' | 'recall' | 'consent-templates' | 'booking-config' | 'config' | 'message-templates' | 'logs' | 'admin' | 'billing' | 'system' | 'candidate-messages' | 'intake-forms' | 'financial-dashboard' | 'invoice-manager' | 'commissions' | 'packages' | 'business-analytics' | 'reputation' | 'inventory';

interface NavItem {
  key: NavKey;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  events,
  onAddEvent,
  currentView,
  onViewChange,
  onClose,
  isMobile = false,
  clinicName,
  clientCount,
  unreadChatCount = 0,
}) => {
  const { user, logoutWithoutMessage, subscription } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [canScroll, setCanScroll] = useState(false);
  const asideRef = useRef<HTMLElement>(null);

  const updateScrollState = useCallback(() => {
    const el = asideRef.current;
    if (!el) return;
    setCanScroll(el.scrollHeight > el.clientHeight + 4);
    setScrolledEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  }, []);

  // Re-check on open/close and on any layout change
  React.useEffect(() => {
    updateScrollState();
  }, [isOpen, updateScrollState]);
  const isPro = subscription?.isProActive || user?.role === 'admin' || user?.role === 'super_admin';
  const ProBadge = () => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 'auto',
      fontSize: 10, fontWeight: 700, color: 'var(--accent)',
      background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
      borderRadius: 99, padding: '1px 5px',
    }}>
      <Lock size={9} />PRO
    </span>
  );
  const { t } = useTranslation();

  const today = new Date();
  const todayEventCount = useMemo(() =>
    events.filter(e =>
      new Date(e.start).toDateString() === today.toDateString() &&
      e.status !== 'cancelled'
    ).length,
    [events, today]
  );

  const isCandidate = user?.role === 'candidate';

  const work: NavItem[] = isCandidate
    ? [
        { key: 'candidate-messages', label: 'Messages', icon: <MessageSquare size={16} />, badge: unreadChatCount || undefined },
      ]
    : [
        { key: 'calendar',        label: t('navigation.calendar'),       icon: <Calendar size={16} />, badge: todayEventCount || undefined },
        { key: 'feed',            label: t('navigation.feed'),           icon: <Activity size={16} /> },
        { key: 'clients',         label: t('navigation.clients'),        icon: <Users size={16} />, badge: clientCount || undefined },
        { key: 'procedures',      label: t('navigation.procedures'),     icon: <Briefcase size={16} /> },
        { key: 'medical-records', label: t('navigation.clinical', 'Clínico'), icon: <FileText size={16} /> },
        { key: 'bulk-message',    label: t('navigation.bulkMessage', 'Mensagem em massa'), icon: <MessagesSquare size={16} /> },
        { key: 'recall',          label: t('navigation.recall', 'Reconsulta'), icon: <RefreshCw size={16} /> },
        { key: 'consent-templates', label: t('navigation.consentForms', 'Termos'), icon: <FileSignature size={16} /> },
        // wt-intake (Onda 2B): shown to all non-candidate users; gate is in App.tsx renderContent
        { key: 'intake-forms', label: t('navigation.intakeForms', 'Anamnese'), icon: <FileText size={16} /> },
        // wt-reputation (Onda 3C)
        { key: 'reputation', label: t('navigation.reputation', 'Reputação'), icon: <Star size={16} /> },
      ];

  const isClinic = subscription?.isClinicActive || user?.role === 'admin' || user?.role === 'super_admin';
  const FullBadge = () => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 'auto',
      fontSize: 10, fontWeight: 700, color: 'var(--accent)',
      background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
      borderRadius: 99, padding: '1px 5px',
    }}>
      <Lock size={9} />FULL
    </span>
  );

  const admin: NavItem[] = (user?.role === 'admin' || user?.role === 'super_admin') ? [
    { key: 'admin',               label: t('navigation.management'),              icon: <Shield size={16} /> },
    // wt-booking (Onda 1A): online booking config. Free plan has the feature ON,
    // so this is gated by role (admin) rather than plan; server enforces the
    // online_booking feature on the API itself.
    { key: 'booking-config',      label: t('navigation.onlineBooking', 'Agendamento online'), icon: <CalendarClock size={16} /> },
    // wt-financial (Onda 2C): financial module nav items
    { key: 'financial-dashboard', label: t('financial.title', 'Financeiro'),      icon: <DollarSign size={16} /> },
    { key: 'invoice-manager',     label: t('financial.invoices', 'Faturas'),      icon: <FileText size={16} /> },
    { key: 'commissions',         label: t('financial.commissions', 'Comissões'), icon: <Users size={16} /> },
    // wt-packages (Onda 3A): session packages — Clinic/Full tier
    { key: 'packages',            label: t('navigation.packages', 'Pacotes'),     icon: <Package size={16} /> },
    // wt-inventory: controle de estoque — Clinic/Full tier
    { key: 'inventory',           label: t('navigation.inventory', 'Estoque'),    icon: <Package size={16} /> },
    // wt-analytics (Onda 3B): business analytics — Pro+
    { key: 'business-analytics',  label: t('navigation.analytics', 'Analytics'), icon: <BarChart2 size={16} /> },
    { key: 'billing',             label: t('navigation.billing'),                 icon: <CreditCard size={16} /> },
  ] : [];

  const account: NavItem[] = [
    { key: 'config', label: t('navigation.configuration'), icon: <Settings size={16} /> },
  ];

  const superAdmin: NavItem[] = user?.role === 'super_admin' ? [
    { key: 'system', label: 'System', icon: <Building2 size={16} /> },
  ] : [];

  const initials = user
    ? `${(user.firstName || '').charAt(0)}${(user.lastName || '').charAt(0)}`.toUpperCase() || 'U'
    : 'U';

  const handleNav = (k: NavKey) => {
    onViewChange(k);
    if (isMobile) onClose();
  };

  return (
    <>
      {isMobile && isOpen && <div className="side-backdrop" onClick={onClose} />}
      <aside ref={asideRef} onScroll={updateScrollState} className={`side ${isOpen ? 'open' : ''}${canScroll ? ' can-scroll' : ''}${scrolledEnd ? ' scrolled-end' : ''}`}>
        <div className="brand">
          <div className="brand-mark">N</div>
          <div>
            <div className="brand-name">{t('common.appTitle')}</div>
            {clinicName && <div className="brand-tenant">{clinicName}</div>}
            <div style={{
              marginTop: 2,
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: subscription?.plan === 'super_admin' ? 'var(--accent)' : subscription?.plan === 'clinic' ? 'var(--accent)' : subscription?.plan === 'pro' ? '#8b5cf6' : 'var(--ink-muted)',
            }}>
              {subscription?.plan === 'super_admin' ? 'Admin' : subscription?.plan === 'clinic' ? 'Clinic' : subscription?.plan === 'pro' ? 'Pro' : 'Free'}
            </div>
          </div>
        </div>

        {!isCandidate && (
          <button className="new-btn" onClick={onAddEvent}>
            <Plus size={14} />
            <span>{t('calendar.createEvent')}</span>
          </button>
        )}

        <div className="nav-label">{t('navigation.work', 'Work')}</div>
        {work.map(item => (
          <button
            key={item.key}
            className={`nav-item ${currentView === item.key ? 'active' : ''}`}
            onClick={() => handleNav(item.key)}
          >
            {item.icon}
            <span>{item.label}</span>
            {item.badge !== undefined && <span className="nav-badge">{item.badge}</span>}
            {item.key === 'medical-records' && !isPro && !isClinic && <ProBadge />}
            {item.key === 'recall' && !isPro && !isClinic && <ProBadge />}
            {item.key === 'consent-templates' && !isPro && !isClinic && <ProBadge />}
            {item.key === 'reputation' && !isPro && !isClinic && <ProBadge />}
          </button>
        ))}

        {!isCandidate && admin.length > 0 && (
          <>
            <div className="nav-label">{t('navigation.administration', 'Administration')}</div>
            {admin.map(item => (
              <button
                key={item.key}
                className={`nav-item ${currentView === item.key ? 'active' : ''}`}
                onClick={() => handleNav(item.key)}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.key === 'business-analytics' && !isPro && !isClinic && <ProBadge />}
                {item.key === 'inventory' && !isClinic && <FullBadge />}
              </button>
            ))}
          </>
        )}

        {!isCandidate && (
          <>
            <div className="nav-label">{t('navigation.account', 'Account')}</div>
            {account.map(item => (
              <button
                key={item.key}
                className={`nav-item ${currentView === item.key ? 'active' : ''}`}
                onClick={() => handleNav(item.key)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </>
        )}

        {!isCandidate && superAdmin.length > 0 && (
          <>
            <div className="nav-label">System</div>
            {superAdmin.map(item => (
              <button
                key={item.key}
                className={`nav-item ${currentView === item.key ? 'active' : ''}`}
                onClick={() => handleNav(item.key)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </>
        )}

        <div className="user-row">
          <button
            onClick={() => setProfileOpen(true)}
            title={t('profile.editProfile', 'Edit profile')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
          >
            <div className="avatar-s">{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div className="uname">
                {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : ''}
              </div>
              <div className="urole">
                {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? t('admin.adminRole') : t('admin.staff')}
              </div>
            </div>
          </button>
          <button className="logout-btn" onClick={logoutWithoutMessage} title={t('auth.logout')}>
            <LogOut size={15} />
          </button>
        </div>
        {profileOpen && <UserProfileModal onClose={() => setProfileOpen(false)} />}
      </aside>
    </>
  );
};

export default React.memo(Sidebar);
